import React, { useState } from 'react';
import { LogoSVG, WhatsAppIcon } from '../../components/Icons';
import { WA_NUMBER } from '../../data/constants';
import { usePageMeta } from '../../utils/usePageMeta';
import { promesaDeRespuesta, waLink } from '../../utils/whatsapp';
import WhatsAppButton from '../../components/WhatsApp/WhatsAppButton';
import { GoogleRatingBadge } from '../../components/Resenas/Resenas';
import HeroVariante, { useVarianteAnuncio } from '../../components/HeroVariante/HeroVariante';
import './DerechoLaboral.css';

const WA_MSG = 'Hola, vi su página de Derecho Laboral y tengo una consulta. ¿Pueden ayudarme?';

interface FAQItem {
  id: string;
  question: string;
  answer: React.ReactNode;
}

const faqs: FAQItem[] = [
  {
    id: 'despedido',
    question: 'Me despidieron, ¿qué hago?',
    answer: (
      <div className="faq-body">
        <p>Si fuiste despedido, lo primero es revisar la causal de despido indicada en tu carta y la fecha en que se puso término a tu contrato.</p>
        <p>No todos los despidos son válidos. Podrías estar frente a un despido injustificado, indebido o improcedente, lo que te da derecho a indemnizaciones adicionales. Contacta a una de nuestras abogadas para que revisen si efectivamente estás bien despedido.</p>
        <p>Además, es importante verificar:</p>
        <ul>
          <li>Si te pagaron correctamente el finiquito</li>
          <li>Si tienes cotizaciones previsionales al día</li>
        </ul>
        <p className="faq-note">⚖️ Tienes plazos para demandar, por lo que es recomendable asesorarte lo antes posible.</p>
      </div>
    ),
  },
  {
    id: 'plazos',
    question: '¿Cuáles son los plazos para demandar?',
    answer: (
      <div className="faq-body">
        <p>Los plazos en materia laboral son cortos, así que es clave no dejar pasar el tiempo.</p>
        <p>En general:</p>
        <ul>
          <li>Tienes <strong>60 días hábiles</strong> desde el despido para presentar una demanda.</li>
          <li>Si haces un reclamo en la Inspección del Trabajo, el plazo puede suspenderse, pero nunca se extiende más allá de <strong>90 días hábiles en total</strong>.</li>
        </ul>
        <p>Hay otras excepciones, por ejemplo 6 meses si no te pagaron cotizaciones previsionales, pero debemos revisar caso a caso. ¡Envíanos un mensaje!</p>
        <p className="faq-note">📌 Importante: estos plazos corren rápido y, si se vencen, pierdes el derecho a reclamar, aunque tu caso sea bueno. Muchísimas personas llegan tarde y ya no se puede hacer nada.</p>
        <p className="faq-cta-inline">👉 Si te despidieron hace poco, escríbenos cuanto antes. Podemos revisar tu caso y orientarte para que no pierdas tu derecho a demandar.</p>
      </div>
    ),
  },
  {
    id: 'necesidades-empresa',
    question: 'Me despidieron por necesidades de la empresa, ¿se puede?',
    answer: (
      <div className="faq-body">
        <p>Sí, el empleador puede despedir por "necesidades de la empresa", pero no basta con que lo diga.</p>
        <p>Este tipo de despido no puede basarse solo en la decisión del empleador. Debe existir una razón real y comprobable, la cual debe expresarse detalladamente en la carta de aviso previo.</p>
        <p className="faq-note">📌 Lo importante: la empresa debe poder demostrar que tu despido era indispensable, y que realizó acciones para evitar despedirte.</p>
        <p>En la práctica, muchas veces esta causal se usa de forma incorrecta, sin una justificación real. En esos casos, podrías estar frente a un despido injustificado, lo que te da derecho a:</p>
        <ul>
          <li>30% extra en recargos legales</li>
          <li>Devolución de descuento AFC</li>
          <li>e incluso otras prestaciones</li>
        </ul>
        <p className="faq-note">⚖️ Ojo: que te despidan por esta causal no significa automáticamente que esté bien aplicado.</p>
        <p>Si tienes dudas sobre tu despido, podemos revisar tu caso y decirte si corresponde demandar. Muchas personas no reclaman y pierden dinero que les corresponde.</p>
      </div>
    ),
  },
  {
    id: 'sin-contrato',
    question: 'No tengo contrato de trabajo, ¿igual tengo derechos?',
    answer: (
      <div className="faq-body">
        <p>Sí. Aunque no hayas firmado contrato, la relación laboral existe igual si trabajas bajo subordinación y dependencia. La escrituración es una obligación para el empleador, pero el contrato de trabajo es <strong>consensual</strong>.</p>
        <p>Esto significa que tienes derecho a:</p>
        <ul>
          <li>Sueldo, pago de horas extra, bonos, gratificaciones, entre otros</li>
          <li>Cotizaciones previsionales</li>
          <li>Jornada laboral regulada</li>
          <li>Indemnización en caso de despido</li>
        </ul>
        <p>El empleador está obligado a escriturar el contrato, y su incumplimiento puede generar sanciones.</p>
      </div>
    ),
  },
  {
    id: 'cotizaciones',
    question: 'No me han pagado mis cotizaciones, ¿qué puedo hacer?',
    answer: (
      <div className="faq-body">
        <p>El no pago de cotizaciones es una infracción grave.</p>
        <p>Esto puede generar consecuencias importantes:</p>
        <ul>
          <li>Posible nulidad del despido, es decir, genera una obligación al empleador de seguir pagando remuneraciones luego del término de la relación laboral</li>
          <li>Acciones judiciales para exigir el pago</li>
          <li>Posibilidad de que el trabajador opte por el "autodespido" (despido indirecto)</li>
        </ul>
        <p className="faq-note">📌 Puedes exigir el cumplimiento y evaluar acciones legales para proteger tus derechos. ¡Contáctanos! Y te explicamos con más detalles… y gratis!</p>
      </div>
    ),
  },
  {
    id: 'renuncia',
    question: 'Quiero renunciar, ¿pierdo todos mis derechos?',
    answer: (
      <div className="faq-body">
        <p>Si renuncias voluntariamente, en general no tienes derecho a indemnización por años de servicio; solo te pagarían feriado proporcional (vacaciones).</p>
        <p>Pero si tu empleador ha incumplido gravemente sus obligaciones (por ejemplo, no pago de sueldo o cotizaciones), podrías ejercer un <strong>autodespido (despido indirecto)</strong>, lo que sí da derecho a indemnizaciones.</p>
        <p className="faq-note">⚖️ Antes de renunciar, es importante evaluar tu caso. Agenda una asesoría gratis, y conversamos todos los detalles.</p>
      </div>
    ),
  },
  {
    id: 'finiquito',
    question: 'Firmé finiquito, ¿puedo demandar igual?',
    answer: (
      <div className="faq-body">
        <p>Depende de cómo firmaste el finiquito.</p>
        <p>Si firmaste:</p>
        <ul>
          <li><strong>Sin reserva de derechos</strong> → En general no puedes demandar después. Existen excepciones, pero muy restringidas.</li>
          <li><strong>Con reserva de derechos</strong> → Sí puedes reclamar lo que dejaste reservado.</li>
        </ul>
        <p className="faq-note">📌 Cada caso debe analizarse. Envíanos un mensaje.</p>
      </div>
    ),
  },
  {
    id: 'reserva-derechos',
    question: '¿Qué es la reserva de derechos?',
    answer: (
      <div className="faq-body">
        <p>La reserva de derechos es una forma de firmar tu finiquito y recibir tu dinero, sin perder la opción de demandar después.</p>
        <p>Cuando te despiden, muchas veces te piden firmar el finiquito para pagarte. Si lo firmas sin reserva, en general estás aceptando todo y después no puedes reclamar.</p>
        <p>En cambio, si firmas <strong>con reserva de derechos</strong>, dejas claro que:</p>
        <ul>
          <li>No estás de acuerdo con todo</li>
          <li>Y que te reservas el derecho a demandar más adelante</li>
        </ul>
        <p>Aunque luego decidas no reclamar o demandar.</p>
        <p className="faq-note">📌 Esto es clave, por ejemplo, si: crees que tu despido fue injustificado, te deben dinero, o no estás conforme con lo que te están pagando.</p>
        <p>Muchas personas firman sin saber esto y pierden la posibilidad de reclamar.</p>
        <p className="faq-cta-inline">👉 Antes de firmar, escríbenos. Te orientamos para que no pierdas tus derechos y tomes una buena decisión.</p>
        <div className="faq-example-box">
          <p className="faq-example-label">Ejemplo de texto de reserva de derechos:</p>
          <blockquote>
            "Me reservo el derecho para demandar por despido injustificado, indebido o improcedente, respecto de la causal de despido invocada. Recargo legal sobre la indemnización por años de servicio, la correcta base de cálculo de todas las prestaciones, devolución del aporte del empleador a AFC, nulidad del despido, pago de feriado legal y proporcional, indemnización sustitutiva del aviso previo, y cualquier diferencia o suma que se adeude. También me reservo acciones por vulneración de derechos fundamentales, garantía de indemnidad, enfermedad profesional, y cualquier otra indemnización o derecho que en conformidad a la ley me corresponda, incluyendo la forma y pago íntegro del finiquito."
          </blockquote>
        </div>
        <div className="faq-steps">
          <p><strong>Firmarás en notaría:</strong> Al momento de firmar, ESCRIBE EL TEXTO ANTERIOR EN TODAS LAS COPIAS DEL FINIQUITO, idealmente en una hoja con texto que no pueda ser eliminada con posterioridad. No importa que se vea feo o cortado.</p>
          <p><strong>Firmarás en el portal de la Dirección del Trabajo:</strong> Pincha la casilla "firmar con reserva de derechos". Se desplegará una ventana emergente, copia y pega o escribe el texto anterior.</p>
        </div>
      </div>
    ),
  },
  {
    id: 'sueldo-horas',
    question: 'Me deben sueldo o horas extras, ¿cómo lo cobro?',
    answer: (
      <div className="faq-body">
        <p>Puedes exigir el pago de remuneraciones adeudadas judicialmente.</p>
        <p>Esto incluye:</p>
        <ul>
          <li>Sueldos impagos</li>
          <li>Horas extraordinarias</li>
          <li>Comisiones u otros beneficios</li>
          <li>Finiquito</li>
        </ul>
        <p className="faq-note">⚖️ La ley protege estos derechos, pero es importante actuar dentro de plazo.</p>
      </div>
    ),
  },
  {
    id: 'licencia',
    question: 'Estoy con licencia y me despidieron, ¿es legal?',
    answer: (
      <div className="faq-body">
        <p>Depende de la causal.</p>
        <p>En general:</p>
        <ul>
          <li>No pueden despedirte por estar enfermo</li>
          <li>Pero sí pueden hacerlo por otras causales legales, por ejemplo cumplimiento del plazo</li>
        </ul>
        <p className="faq-note">📌 Este tipo de casos debe analizarse en detalle, ya que muchas veces se trata de despidos injustificados o vulneración de derechos. Debes tener en claro que en ningún caso te pueden despedir por haberte tomado una licencia.</p>
      </div>
    ),
  },
  {
    id: 'licencia-despido',
    question: 'Me despidieron luego de tomarme una licencia médica, ¿qué hago?',
    answer: (
      <div className="faq-body">
        <p>Que te despidan justo después de una licencia casi nunca es legal, y muchas veces puede ser cuestionado.</p>
        <p>La empresa no puede despedirte por estar enfermo ni como represalia por haber hecho uso de tu licencia.</p>
        <p>Si el despido ocurre en ese contexto, podría tratarse de:</p>
        <ul>
          <li>Un despido injustificado</li>
          <li>O incluso una vulneración de derechos fundamentales (tutela laboral)</li>
        </ul>
        <p className="faq-note">⚠️ Este tipo de casos es muy común y muchas personas no reclaman, pensando que el empleador "puede hacerlo igual".</p>
        <p className="faq-cta-inline">👉 Si te pasó algo así, escríbenos y revisamos tu caso. Puedes tener derecho a indemnizaciones mucho mayores de lo que imaginas.</p>
      </div>
    ),
  },
  {
    id: 'acoso',
    question: 'Sufrí acoso laboral, ¿qué puedo hacer?',
    answer: (
      <div className="faq-body">
        <p>El acoso laboral está prohibido y puede ser denunciado.</p>
        <p>Puedes:</p>
        <ul>
          <li>Presentar un reclamo interno</li>
          <li>Denunciar ante la Inspección del Trabajo</li>
          <li>Ejercer acciones judiciales</li>
        </ul>
        <p className="faq-note">⚖️ En casos graves, incluso podrías poner término al contrato con indemnización.</p>
      </div>
    ),
  },
  {
    id: 'tutela',
    question: '¿Qué es una tutela laboral?',
    answer: (
      <div className="faq-body">
        <p>La tutela laboral es una acción judicial que te protege cuando tu empleador vulnera tus derechos fundamentales en el trabajo.</p>
        <p>No se trata solo de sueldos o despidos. Aquí hablamos de situaciones más graves, como:</p>
        <ul>
          <li>Despidos por represalia (por ejemplo, por reclamar tus derechos, solicitar una fiscalización, tomar licencia médica, hacer una denuncia, etc.)</li>
          <li>Acoso laboral</li>
          <li>Discriminación (por maternidad, apariencia, orientación sexual, y cualquier otra)</li>
          <li>Vulneración de tu dignidad o vida privada</li>
        </ul>
        <p className="faq-note">📌 En simple: es una forma de defenderte cuando te tratan injustamente y afectan tus derechos como persona, no solo como trabajador.</p>
        <p className="faq-note">⚖️ Este tipo de demanda puede significar indemnizaciones importantes, ya que la ley establece una compensación que normalmente va entre <strong>6 y 11 remuneraciones adicionales</strong>.</p>
        <p className="faq-note">⚠️ Muchas veces estas situaciones pasan desapercibidas o se normalizan, pero sí se pueden reclamar.</p>
      </div>
    ),
  },
  {
    id: 'camaras',
    question: '¿Me pueden fiscalizar por cámaras?',
    answer: (
      <div className="faq-body">
        <p>Tu empleador puede tener cámaras, pero no puede usarlas para fiscalizarte de forma constante o invasiva.</p>
        <p>Las cámaras deben tener un fin legítimo, como la seguridad o el resguardo de bienes, no para vigilarte permanentemente ni controlar cada uno de tus movimientos.</p>
        <p className="faq-note">📌 Por ejemplo, no es legal:</p>
        <ul>
          <li>Grabarte en lugares privados (baños, camarines, etc.)</li>
          <li>Usarlas para controlarte constantemente (enviarte fotos de las cámaras, o llamarte la atención porque "te vieron descansando vía cámara")</li>
          <li>No informarte que estás siendo grabado</li>
          <li>Grabarte con audio</li>
          <li>Entre otros</li>
        </ul>
        <p className="faq-note">⚠️ Si las cámaras afectan tu dignidad, tu privacidad o se usan de forma indebida, puede tratarse de una vulneración de derechos fundamentales.</p>
        <p className="faq-note">⚖️ En esos casos, incluso podrías demandar mediante una tutela laboral y obtener indemnizaciones.</p>
        <p className="faq-cta-inline">👉 Si sientes que te están vigilando de forma inapropiada, escríbenos. Evaluamos tu caso y te orientamos sobre qué hacer.</p>
        <p className="faq-related-link">Quizás podría interesarte: <button className="faq-link-btn" onClick={() => document.getElementById('faq-camaras-audio')?.scrollIntoView({ behavior: 'smooth' })}>Las cámaras de mi trabajo tienen audio, ¿es legal?</button></p>
      </div>
    ),
  },
  {
    id: 'camaras-audio',
    question: 'Las cámaras de mi trabajo tienen audio, ¿es legal?',
    answer: (
      <div className="faq-body">
        <p>No es legal que las cámaras en el trabajo graben audio.</p>
        <p>Tu empleador puede usar cámaras por razones de seguridad, pero grabar conversaciones es mucho más invasivo y afecta directamente tu privacidad.</p>
        <p className="faq-note">📌 La grabación de audio puede implicar:</p>
        <ul>
          <li>Escuchar conversaciones privadas</li>
          <li>Registrar comunicaciones sin tu consentimiento</li>
          <li>Un control excesivo sobre los trabajadores</li>
        </ul>
        <p className="faq-note">⚠️ Por eso, normalmente se considera una vulneración de derechos fundamentales, especialmente tu vida privada y dignidad.</p>
        <p className="faq-cta-inline">👉 Si en tu trabajo hay cámaras con audio, escríbenos. Podemos revisar tu situación y decirte si corresponde tomar acciones.</p>
      </div>
    ),
  },
  {
    id: 'obligaciones',
    question: '¿Cuáles son las obligaciones de mi empleador?',
    answer: (
      <div className="faq-body">
        <p>Tu empleador no solo debe pagarte el sueldo. Tiene varias obligaciones legales que debe cumplir sí o sí.</p>
        <p>Entre las principales:</p>
        <ul>
          <li>Pagarte tu remuneración de forma íntegra y oportuna</li>
          <li>Pagar íntegramente tus cotizaciones previsionales</li>
          <li>Respetar tu jornada laboral y descansos</li>
          <li>Otorgarte feriado legal</li>
          <li>Mantener un trato digno y respetuoso</li>
          <li>Cumplir con las condiciones acordadas en tu contrato</li>
          <li>Entre otros</li>
        </ul>
        <p className="faq-note">📌 Además, debe garantizar un ambiente de trabajo seguro y libre de abusos.</p>
        <p className="faq-note">⚠️ Si tu empleador no cumple con estas obligaciones, no es algo menor. Puede dar lugar a:</p>
        <ul>
          <li>Multas</li>
          <li>Indemnizaciones</li>
          <li>e incluso a que puedas poner término al contrato con derecho a pago (autodespido)</li>
        </ul>
        <p className="faq-cta-inline">👉 Si sientes que no están respetando tus derechos, escríbenos. Podemos revisar tu caso y orientarte sobre qué hacer.</p>
      </div>
    ),
  },
];

const DerechoLaboral: React.FC = () => {
  const [openItems, setOpenItems] = useState<Set<string>>(new Set());

  const variante = useVarianteAnuncio('laboral');

  // Con campaña, el mensaje llega diciendo a qué viene la persona.

  const waUrl = waLink(variante ? variante.mensaje : WA_MSG);


  usePageMeta(
    'Derecho Laboral en Concepción | Preguntas Frecuentes | Souza & Villa Abogadas',
    'Abogadas laboralistas en Concepción. Resolvemos tus dudas sobre despidos, finiquitos, plazos para demandar, cotizaciones y más. Primera consulta gratuita.',
    '/areas/derecho-laboral',
  );

  const toggle = (id: string) => {
    setOpenItems(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="dl-page">
      {/* Header */}
      <header className="dl-header">
        <a href="/" className="dl-logo">
          <LogoSVG size={44} />
          <div className="dl-logo-text">
            <span className="dl-logo-name">Souza &amp; Villa</span>
            <span className="dl-logo-tagline">Abogadas</span>
          </div>
        </a>
        <div className="dl-header-actions">
          <a href="/#areas" className="dl-back-link">← Volver al inicio</a>
          <a
            href={waUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="dl-btn-wa"
          >
            Consulta gratis
          </a>
        </div>
      </header>

      {/* Hero */}
      <section className="dl-hero">
        <div className="dl-hero-inner">
          {variante ? <HeroVariante v={variante} /> : (
            <>
              <div className="dl-hero-label">Área de práctica</div>
              <h1 className="dl-hero-title">Derecho <em>Laboral</em></h1>
              <p className="dl-hero-desc">
                Preguntas frecuentes, orientación legal y todo lo que necesitas saber sobre tus derechos como trabajador en Chile.
              </p>
            </>
          )}
          <a href={waUrl} target="_blank" rel="noopener noreferrer" className="btn-whatsapp dl-btn-wa-cta">
            <WhatsAppIcon size={17} /> Consulta gratuita
          </a>
          <div className="dl-hero-respuesta">{promesaDeRespuesta()} · Primera consulta gratis</div>
          <GoogleRatingBadge className="rating-badge--hero dl-hero-rating" />
        </div>
      </section>

      {/* FAQ */}
      <section className="dl-faq-section">
        <div className="dl-faq-intro">
          <p>Reunimos las preguntas más frecuentes que nos hacen nuestros clientes. Si tu caso no aparece aquí, escríbenos: <strong>la primera orientación es gratuita</strong>.</p>
        </div>

        <div className="dl-faq-list">
          {faqs.map((faq) => {
            const isOpen = openItems.has(faq.id);
            return (
              <div
                key={faq.id}
                id={`faq-${faq.id}`}
                className={`dl-faq-item${isOpen ? ' dl-faq-item--open' : ''}`}
              >
                <button
                  className="dl-faq-question"
                  onClick={() => toggle(faq.id)}
                  aria-expanded={isOpen}
                >
                  <span>{faq.question}</span>
                  <span className="dl-faq-icon" aria-hidden="true">
                    {isOpen ? '−' : '+'}
                  </span>
                </button>
                <div className="dl-faq-answer">
                  <div className="dl-faq-answer-inner">
                    {faq.answer}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* CTA Bottom */}
      <section className="dl-cta-section">
        <div className="dl-cta-inner">
          <div className="dl-cta-label">¿Tienes dudas sobre tu situación?</div>
          <h2 className="dl-cta-title">Revisamos tu caso <em>sin costo</em></h2>
          <p className="dl-cta-desc">
            Contáctanos por WhatsApp. Nuestras abogadas te orientarán de forma clara y directa sobre qué opciones tienes y qué pasos puedes seguir.
          </p>
          <div className="dl-cta-actions">
            <a
              href={waUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-whatsapp dl-btn-wa-cta"
            >
              <WhatsAppIcon size={17} /> Escribir por WhatsApp
            </a>
            <a href="/#agendar" className="dl-btn-ghost">
              Agendar consulta
            </a>
          </div>
          <p className="dl-cta-note">
            Oficina en Concepción Centro y Lomas Coloradas · +56 9 3924 1385
          </p>
        </div>
      </section>

      {/* Footer minimal */}
      <footer className="dl-footer">
        <p>© 2026 Souza &amp; Villa Abogadas · <a href="/">svabogadas.cl</a></p>
      </footer>

      <WhatsAppButton />
    </div>
  );
};

export default DerechoLaboral;
